import { Service } from '@yandex-360/global-bar';
import { ReduxState } from './index';
import { ApplicationEntrypoints } from './slices/discoverySlice';
import { getServiceById, ServiceData } from '@/components/App/servicesData';

// Discovery key -> landing-page service ID. Used only by the product cards.
const servicesMap: Record<string, Service> = {
    disk: 'Disk',
    docs: 'Documents',
    boards: 'Concept',
    mail: 'Mail',
    calendar: 'Calendar',
    telemost: 'Telemost',
    tracker: 'Tracker',
    wiki: 'Wiki',
    forms: 'Forms',
};

// Reverse mapping: Service id -> discovery key
const serviceToDiscoveryKey: Record<string, string> = {
    Disk: 'disk',
    Documents: 'docs',
    Concept: 'boards',
    Mail: 'mail',
    Calendar: 'calendar',
    Messenger: 'messenger',
    Telemost: 'telemost',
    Tracker: 'tracker',
    Wiki: 'wiki',
    Forms: 'forms',
    Admin: 'admin',
};

// Config selectors
export const selectConfig = (state: ReduxState) => state.config;
export const selectLanguage = (state: ReduxState) => state.config.language;
export const selectBoxVersion = (state: ReduxState) => state.config.boxVersion;
export const selectEnvironment = (state: ReduxState) => state.config.environment;
export const selectDomain = (state: ReduxState) => state.config.domain;
export const selectUserIdHost = (state: ReduxState) => state.config.userIdHost;
export const selectAuthEnabled = (state: ReduxState) => Boolean(state.config.authEnabled);
export const selectGlobalBarInitData = (state: ReduxState) => state.config.globalBarInitData;
export const selectOrganizationLogoUrl = (state: ReduxState) => state.config.organizationLogoUrl;
export const selectLinks = (state: ReduxState) => state.config.links;
export const selectDocumentationLink = (state: ReduxState) => state.config.links.documentation;

// User selectors
export const selectUser = (state: ReduxState) => state.user.user;
export const selectIsAuthenticated = (state: ReduxState) => state.user.isAuthenticated;
/** 154 blackbox / is_b2b_admin — администратор организации */
export const selectIsB2bAdmin = (state: ReduxState): boolean =>
    Boolean(state.user.user?.isConnectAdmin);
export const selectAuthLink = (state: ReduxState) => state.config.links.passport.auth;
export const selectLogoutLink = (state: ReduxState) => state.config.links.passport.logout;

// Discovery selectors
export const selectDiscovery = (state: ReduxState) => state.discovery;
export const selectBaseDomain = (state: ReduxState) => state.discovery.base_domain;
export const selectDeploymentId = (state: ReduxState) => state.discovery.deployment_id;
export const selectDiscoveryEnvironment = (state: ReduxState) => state.discovery.environment;
export const selectApplications = (state: ReduxState) => state.discovery.applications.items;
export const selectApplicationByKey = (key: string) => (state: ReduxState) =>
    state.discovery.applications.items.find(app => app.key === key);
export const selectAvailableApplications = (state: ReduxState) =>
    state.discovery.applications.items.filter(app => app.is_available && app.is_licensed);

// This is the landing page's card list. GlobalBar receives its own services
// from global-bar/init-data and must not use this selector.
export const selectServices = (state: ReduxState) =>
    state.services.services.map(service => servicesMap[service]) as Service[];

// Enriched service data type
export interface EnrichedServiceData extends ServiceData {
    entrypoints: ApplicationEntrypoints;
}

// Selector to get enriched service data by id
export const selectEnrichedServiceById = (serviceId: Service | undefined) =>
    (state: ReduxState): EnrichedServiceData | undefined => {
        if (!serviceId) return undefined;

        const staticData = getServiceById(serviceId);

        if (!staticData) return undefined;

        const discoveryKey = serviceToDiscoveryKey[serviceId];
        const discoveryApp = state.discovery.applications.items.find(app => app.key === discoveryKey);

        return {
            ...staticData,
            entrypoints: discoveryApp?.entrypoints ?? {},
        };
    };
